import { createApi } from "@reduxjs/toolkit/query/react";
import { serializeError } from "serialize-error";
import { supabase, supabaseBaseQuery } from "../../../supabase";
import { providerKeySlugs } from "../constants/providerKeys";
import type {
  DeleteProviderApiKeyPayload,
  ProviderKeysStatus,
  SaveProviderApiKeyPayload,
} from "../types/providerKeysApiTypes";

const providerKeysApi = createApi({
  reducerPath: "api.ProviderKeys",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["ProviderKeys"],
  endpoints: (build) => ({
    getProviderConfigurationStatuses: build.query<ProviderKeysStatus, void>({
      queryFn: async () => {
        const statuses: ProviderKeysStatus = {
          openai: false,
          anthropic: false,
          gemini: false,
          mistral: false,
          xai: false,
        };

        const results = await Promise.all(
          providerKeySlugs.map(async (provider) => {
            const { data, error } = await supabase.rpc("has_current_user_provider_key", {
              p_provider: provider,
            });

            if (error) return { provider, error };
            return { provider, data };
          }),
        );

        for (const result of results) {
          if ("error" in result && result.error) {
            return { error: serializeError(result.error) };
          }

          statuses[result.provider] = Boolean(result.data);
        }

        return { data: statuses };
      },
      providesTags: ["ProviderKeys"],
    }),

    saveProviderApiKey: build.mutation<void, SaveProviderApiKeyPayload>({
      queryFn: async (payload) => {
        const { error } = await supabase.functions.invoke("save-provider-api-key", {
          method: "POST",
          body: payload,
        });

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      invalidatesTags: ["ProviderKeys"],
    }),

    deleteProviderApiKey: build.mutation<void, DeleteProviderApiKeyPayload>({
      queryFn: async (payload) => {
        const { error } = await supabase.functions.invoke("delete-provider-api-key", {
          method: "POST",
          body: payload,
        });

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      invalidatesTags: ["ProviderKeys"],
    }),
  }),
});

const {
  useGetProviderConfigurationStatusesQuery,
  useLazyGetProviderConfigurationStatusesQuery,
  useSaveProviderApiKeyMutation,
  useDeleteProviderApiKeyMutation,
} = providerKeysApi;

export {
  providerKeysApi,
  type ProviderKeysStatus,
  useGetProviderConfigurationStatusesQuery,
  useLazyGetProviderConfigurationStatusesQuery,
  useSaveProviderApiKeyMutation,
  useDeleteProviderApiKeyMutation,
};
